import {
  CheckBoxWithLabelWidget,
  getMaxCharValidation,
  TextLinkWidget
} from '@tableau/widgets';
import * as React from 'react';

import { HelpService } from '~commonInfra/services/HelpService';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { assertUnreachable } from '~src/ts/helpers/TypescriptHelpers';

import { Messages } from '../../codegen/Localize';
import { ReactActionDialogBodyProps, ReactActionDialogOptions } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { TextFormatterOrAreaWidget } from '../content/components/TextFormatterOrAreaWidget';
import {
  checkboxStyle,
  ContentItemNameDiv,
  DATA_QUALITY_DIALOG_HELP_TEST_ID,
  DATA_QUALITY_DIALOG_MESSAGE_ID,
  DATA_QUALITY_DIALOG_TOGGLE_TEST_ID,
  HelpTextDiv,
  InputHintDiv,
  markupEditorStyle,
  textAreaStyle,
  textAreaWidgetClass} from '../content/styles/DataQualityStyles';
import { isFormDataValidForSubmission,SevereWarningContent } from './DataQualityDialog';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;

function isTextAreaValid(currentFormData: { message: string }): boolean {
  return !(currentFormData.message.length > MESSAGE_MAXIMUM_LENGTH);
}

export const defaultInitialState: DataQualityTriggerDialogState = {
  enableTrigger: false,
  isSevere: false,
  message: '',
};

export type DataQualityTriggerDialogBodyProps = {
  readonly contentName: string;
  readonly contentType: 'datasource' | 'flow';
}

export type DataQualityTriggerDialogState = {
  readonly enableTrigger: boolean;
  readonly isSevere: boolean;
  readonly message: string;
}

function getDialogStrings(contentType: string) {
  if (contentType === 'datasource') {
    return {
      title: Messages.dataQualityTriggerDialog_Title_Datasource(),
      prompt: Messages.dataQualityTriggerDialog_Prompt_Datasource(),
      checkboxLabel: Messages.dataQualityTriggerDialog_EnableTriggerCheckboxLabel_Datasource(),
      helpToken: 'dqw-auto-extract',
    };
  } else {
    return {
      title: Messages.dataQualityTriggerDialog_Title_Flow(),
      prompt: Messages.dataQualityTriggerDialog_Prompt_Flow(),
      checkboxLabel: Messages.dataQualityTriggerDialog_EnableTriggerCheckboxLabel_Flow(),
      helpToken: 'dqw-auto-flow',
    };
  }
}

export type DataQualityTriggerConfigurationUIProps = {
  readonly contentName?: string;
  readonly contentType: 'datasource' | 'flow';
  readonly enableTrigger: boolean;
  readonly isSevere: boolean;
  readonly message: string;
  readonly dispatch: React.Dispatch<DataQualityTriggerConfigurationUIAction>;
  readonly hideEnableTrigger?: boolean; // Hide enable trigger checkbox if its handled externally
}

export type DataQualityTriggerConfigurationUIAction =
  | { type: 'setEnableTrigger', value: DataQualityTriggerConfigurationUIProps['enableTrigger'] }
  | { type: 'setIsSevere', value: DataQualityTriggerConfigurationUIProps['isSevere'] }
  | { type: 'setMessage', value: DataQualityTriggerConfigurationUIProps['message'] };

export function DataQualityTriggerConfigurationUI(props: DataQualityTriggerConfigurationUIProps): React.ReactElement {
  const handleMessageChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => props.dispatch({ type: 'setMessage', value: e.currentTarget.value }),
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.dispatch]
  );

  const { message, valid } = getMaxCharValidation(props.message, MESSAGE_MAXIMUM_LENGTH);
  const useMarkup = FeatureFlags.markupEditingEnabled;

  return (
    <>
      {props.contentName ? <ContentItemNameDiv>{props.contentName}</ContentItemNameDiv> : null}

      <HelpTextDiv>
        {getDialogStrings(props.contentType).prompt}
        <TextLinkWidget
          href={HelpService.getServerHelpUrlByCategory(getDialogStrings(props.contentType).helpToken)}
          testId={DATA_QUALITY_DIALOG_HELP_TEST_ID}
          target="_blank" rel="noopener noreferrer"
        >
          {Messages.dataQuality_learnMore()}
        </TextLinkWidget>
      </HelpTextDiv>
      {props.hideEnableTrigger ? null :
        <CheckBoxWithLabelWidget
          id={DATA_QUALITY_DIALOG_TOGGLE_TEST_ID} // used for initial focus
          label={getDialogStrings(props.contentType).checkboxLabel}
          testId={DATA_QUALITY_DIALOG_TOGGLE_TEST_ID}
          checked={props.enableTrigger}
          handleChange={(enableTrigger: boolean) => props.dispatch({ type: 'setEnableTrigger', value: enableTrigger })}
          removeLeftMargin={true}
          containerStyle={checkboxStyle}
        />
      }
      <SevereWarningContent
        disabled={!props.enableTrigger}
        isSevere={props.isSevere}
        showHelpText={false}
        setIsSevere={(isSevere: boolean) => props.dispatch({ type: 'setIsSevere', value: isSevere })}
      />

      <TextFormatterOrAreaWidget
        label={<InputHintDiv>{Messages.dataQualityTriggerMessage()}</InputHintDiv>}
        onChange={handleMessageChange}
        id={DATA_QUALITY_DIALOG_MESSAGE_ID}
        testId={DATA_QUALITY_DIALOG_MESSAGE_ID}
        disabled={!props.enableTrigger}
        valid={valid}
        message={message}
        className={useMarkup ? null : textAreaWidgetClass}
        style={useMarkup ? markupEditorStyle : textAreaStyle}
        markup={props.message}
      />
    </>
  );
}

export type DataQualityTriggerActionDialogOptions = ReactActionDialogOptions<DataQualityTriggerDialogBodyProps, DataQualityTriggerDialogState, unknown>;
export type DataQualityTriggerDialogBodyComponentProps = ReactActionDialogBodyProps<DataQualityTriggerDialogBodyProps, DataQualityTriggerDialogState>;

export function DataQualityTriggerActionDialogBody(props: DataQualityTriggerDialogBodyComponentProps): React.ReactElement {

  function dispatch(action: DataQualityTriggerConfigurationUIAction) {
    switch (action.type) {
      case 'setEnableTrigger':
        props.updateDialogState({ enableTrigger: action.value });
        break;
      case 'setIsSevere':
        props.updateDialogState({ isSevere: action.value });
        break;
      case 'setMessage':
        props.updateDialogState({ message: action.value });
        break;
      default:
        assertUnreachable(action);
    }
  }

  return (
    <DataQualityTriggerConfigurationUI
      contentName={props.contentName}
      contentType={props.contentType}
      enableTrigger={props.dialogState.enableTrigger}
      isSevere={props.dialogState.isSevere}
      message={props.dialogState.message}
      dispatch={dispatch}
    />
  );
}

export function getDataQualityTriggerActionDialogOptions(args: {
  initialBodyProps: DataQualityTriggerDialogBodyProps;
  initialFormData: DataQualityTriggerDialogState;
  doAction: (formData: DataQualityTriggerDialogState) => Promise<unknown>;
}): DataQualityTriggerActionDialogOptions {

  const { initialBodyProps, initialFormData, doAction } = args;

  return {
    title: getDialogStrings(args.initialBodyProps.contentType).title,
    bodyComponent: DataQualityTriggerActionDialogBody,
    initialDialogState: initialFormData,
    initialBodyProps: initialBodyProps,
    actionName: Messages.save(),
    action: (_, dialogState) => doAction(dialogState),
    actionDisabled: (_, dialogState) => !isFormDataValidForSubmission({
      initialFormData,
      currentFormData: dialogState,
      additionalValidation: isTextAreaValid
    }),
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
    initiallyFocusedElement: (dialog) => dialog.querySelector(`#${DATA_QUALITY_DIALOG_TOGGLE_TEST_ID}`),
  };
}
